import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ReadingMessage } from "@/entities/ReadingMessage";
import { Tarot } from "@/entities/Tarot";
import { UserData } from "@/entities/User";
import { APIReadingRequestDetails } from "@/shared/types";
import { DropdownPosition } from "@/shared/ui/Dropdown";

import { ResponseMode } from "../../const/const";
import { fetchDraftResponse } from "../services/fetchDraftResponse";
import { fetchReadingDetails } from "../services/fetchReadingDetails";
import { fetchTarotImages } from "../services/fetchTarotImages";
import { fetchUserProfile } from "../services/fetchUserProfile";
import { sendMessage } from "../services/sendMessage";
import { sendResponse } from "../services/sendResponse";
import { ReadingDetailsSchema } from "../types/readingDetailsSchema";

const initialState: ReadingDetailsSchema = {
  responseForm: {
    messageText: "",
    responseMode: ResponseMode.Standard,
    allowedTarotCards: [],
  },
};

export const readingDetailsSlice = createSlice({
  name: "readingDetails",
  initialState,
  reducers: {
    reset: (state) => {
      state.readingDetailsData = undefined;
      state.userProfileData = undefined;
      state.responseForm = {
        messageText: "",
        responseMode: ResponseMode.Standard,
        allowedTarotCards: [],
      };
      state.dropdownPosition = undefined;
      state.focusedMessageData = undefined;
    },
    setReadingDetailsData: (state, action: PayloadAction<APIReadingRequestDetails>) => {
      state.readingDetailsData = action.payload;
    },
    setResponseFormMessageText: (state, action: PayloadAction<string>) => {
      state.responseForm.messageText = action.payload;
    },
    setResponseFormResponseMode: (state, action: PayloadAction<ResponseMode>) => {
      state.responseForm.responseMode = action.payload;
    },
    setResponseFormPersonalityId: (state, action: PayloadAction<number>) => {
      state.responseForm.writerPersonality = action.payload;
    },
    setResponseFormTarotCardName: (state, action: PayloadAction<string>) => {
      state.responseForm.tarotCardName = action.payload;
    },
    setResponseFormTarotCard: (state, action: PayloadAction<Tarot>) => {
      state.responseForm.tarotCard = action.payload;
    },
    setDropdownPosition: (state, action: PayloadAction<DropdownPosition | undefined>) => {
      state.dropdownPosition = action.payload;
    },
    setFocusedMessageData: (state, action: PayloadAction<ReadingMessage | undefined>) => {
      state.focusedMessageData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReadingDetails.pending, (state) => {
        state.readingDetailsIsLoading = true;
      })
      .addCase(fetchReadingDetails.fulfilled, (state, action: PayloadAction<APIReadingRequestDetails>) => {
        state.readingDetailsIsLoading = false;
        state.readingDetailsData = action.payload;

        if (action.payload.personality) {
          state.responseForm.writerPersonality = action.payload.personality.id;
        } else {
          state.responseForm.writerPersonality = null;
        }
      })
      .addCase(fetchReadingDetails.rejected, (state, action) => {
        if (action.payload.code === "ERR_CANCELED") {
          return;
        }
        state.readingDetailsIsLoading = false;
        state.readingDetailsError = action.payload;
        state.readingDetailsData = undefined;
      })
      .addCase(fetchDraftResponse.fulfilled, (state, action) => {
        state.responseForm.messageText = action.payload;
      });

    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.userProfileIsLoading = true;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action: PayloadAction<UserData>) => {
        state.userProfileIsLoading = false;
        state.userProfileData = action.payload;

        if (!state.responseForm.writerPersonality && action.payload.assignedPersonalities[0]?.id !== undefined) {
          state.responseForm.writerPersonality = action.payload.assignedPersonalities[0]?.id;
        }
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        if (action.payload.code === "ERR_CANCELED") {
          return;
        }
        state.userProfileIsLoading = false;
        state.userProfileError = action.payload;
        state.userProfileData = undefined;
      });

    builder
      .addCase(fetchTarotImages.pending, (state) => {
        state.responseForm.allowedTarotCards = [];
      })
      .addCase(fetchTarotImages.fulfilled, (state, action: PayloadAction<Tarot[]>) => {
        state.responseForm.allowedTarotCards = action.payload;
      });

    builder
      .addCase(sendMessage.pending, (state) => {
        state.responseFormIsLoading = true;
      })
      .addCase(sendMessage.fulfilled, (state) => {
        state.responseFormIsLoading = false;
        state.responseForm.messageText = "";
        state.responseForm.responseMode = ResponseMode.Standard;
        state.responseForm.tarotCardName = undefined;
        state.responseForm.tarotCard = undefined;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.responseFormIsLoading = false;
        state.responseFormError = action.payload;
      });

    builder
      .addCase(sendResponse.pending, (state) => {
        state.responseFormIsLoading = true;
      })
      .addCase(sendResponse.fulfilled, (state) => {
        state.responseFormIsLoading = false;
        state.responseForm.messageText = "";
        state.responseForm.responseMode = ResponseMode.Standard;
        state.responseForm.tarotCardName = undefined;
        state.responseForm.tarotCard = undefined;
      })
      .addCase(sendResponse.rejected, (state, action) => {
        state.responseFormIsLoading = false;
        state.responseFormError = action.payload;
      });
  },
});

export const { actions: readingDetailsActions } = readingDetailsSlice;
export const { reducer: readingDetailsReducer } = readingDetailsSlice;
